import { useState } from 'react';
import Link from 'next/link';
import { fraunces } from '../Landing/fonts';
import { INK, MUTED, PlaneIcon } from '../Landing/ui';
import JournalTabs from './JournalTabs';
import ItineraryMap from './ItineraryMap';

// Itineraries as a flight plan: theme tabs, numbered stops down a dashed
// route line, and the route's map in a chart frame beside it.
export default function RouteItinerary({ itineraries, locationDetails }) {
  const [value, setValue] = useState(0);

  if (!itineraries || itineraries.length === 0) return null;
  const itinerary = itineraries[value];

  return (
    <div>
      <JournalTabs
        ariaLabel="Itinerary themes"
        options={itineraries.map(it => ({ label: it.theme }))}
        value={value}
        onChange={setValue}
      />

      <div className="tw-mt-8 tw-grid tw-items-start tw-gap-10 lg:tw-grid-cols-2">
        <div>
          <h3
            className={`${fraunces.className} ${INK} tw-m-0 tw-text-xl tw-font-semibold tw-tracking-tight sm:tw-text-2xl`}
          >
            {itinerary.title}{' '}
            <span className={`${MUTED} tw-text-base tw-font-normal`}>
              (
              {itinerary.duration % 7 === 0
                ? `${itinerary.duration / 7} week${
                    itinerary.duration === 7 ? '' : 's'
                  }`
                : `${itinerary.duration} days`}
              )
            </span>
          </h3>
          {itinerary.author && itinerary.author.username !== 'itineraries' && (
            <p className={`${MUTED} tw-m-0 tw-mt-1 tw-text-sm`}>
              by{' '}
              <Link
                href={`/@${itinerary.author.username}`}
                prefetch={false}
                className="tw-text-primary-700 dark:tw-text-primary-300 tw-no-underline hover:tw-underline"
              >
                @{itinerary.author.username}
              </Link>
            </p>
          )}

          <ol className="tw-relative tw-m-0 tw-mt-6 tw-list-none tw-p-0">
            {/* The route line behind the stop badges */}
            <span
              aria-hidden="true"
              className="tw-absolute tw-bottom-5 tw-left-4 tw-top-2 tw-w-0 tw-border-0 tw-border-l tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44]"
            />
            {/* Guarded: an itinerary whose places have not loaded (or a
                generation that produced none) threw inside render and replaced
                the whole country guide with the generic error screen. */}
            {(itinerary.places || []).map((place, i) => (
              <li
                key={place.name}
                className="tw-relative tw-flex tw-items-start tw-gap-4 tw-pb-6"
              >
                <span className="tw-z-10 tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-dashed tw-border-primary-600 dark:tw-border-primary-300 tw-bg-[#FAF8F1] dark:tw-bg-[#0E1A16] tw-font-mono tw-text-xs tw-font-semibold tw-text-primary-700 dark:tw-text-primary-300">
                  {i + 1}
                </span>
                <span>
                  <span
                    className={`${INK} tw-block tw-text-[15px] tw-font-semibold`}
                  >
                    {place.internalLink ? (
                      <Link
                        href={place.internalLink}
                        prefetch={false}
                        className="tw-text-inherit tw-no-underline hover:tw-text-primary-700 dark:hover:tw-text-primary-300 hover:tw-underline"
                      >
                        {place.name}
                      </Link>
                    ) : (
                      place.name
                    )}
                  </span>
                  {place.description && (
                    <span
                      className={`${MUTED} tw-mt-0.5 tw-block tw-text-sm tw-leading-relaxed`}
                    >
                      {place.description}
                    </span>
                  )}
                </span>
              </li>
            ))}
            <li
              aria-hidden="true"
              className="tw-relative tw-flex tw-items-center tw-gap-4"
            >
              <span className="tw-z-10 tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#FAF8F1] dark:tw-bg-[#0E1A16]">
                <PlaneIcon className="tw-h-4 tw-w-4 tw-text-primary-600 dark:tw-text-primary-300" />
              </span>
              <span className="tw-font-mono tw-text-[9px] tw-tracking-[0.25em] tw-text-[#8A958F]">
                SAFE TRAVELS
              </span>
            </li>
          </ol>
        </div>

        <div className="tw-relative tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-white dark:tw-bg-[#15231E] tw-shadow-[0_24px_50px_-30px_rgba(29,45,40,0.4)] lg:tw-sticky lg:tw-top-24">
          <div className="tw-flex tw-items-baseline tw-justify-between tw-px-5 tw-py-3 tw-font-mono tw-text-[10px] tw-font-semibold tw-tracking-[0.25em] tw-text-[#8A958F]">
            <span>ROUTE CHART</span>
            <span>{itinerary.theme ? itinerary.theme.toUpperCase() : ''}</span>
          </div>
          <div className="tw-relative">
            <ItineraryMap
              itinerary={itinerary}
              locationDetails={locationDetails}
              useBbox={value === 0 && locationDetails.countryCode !== 'tw'}
              rounding={0}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
